import { motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Reusable FAQ accordion for service detail pages and other sub-pages.
 * `faqs`: [{ q: string, a: string | ReactNode, html?: boolean }]
 */
export default function ServiceFAQ({
  eyebrow = "FAQ",
  title = "Common questions",
  subtitle,
  faqs,
  defaultOpenIndex = 0,
  testId = "service-faq",
}) {
  return (
    <section
      data-testid={testId}
      aria-labelledby="service-faq-heading"
      className="relative bg-mist py-20 sm:py-24"
    >
      <div className="container-page grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="lg:col-span-4"
        >
          <span className="pill">
            <HelpCircle className="h-3 w-3 text-aqua" />
            {eyebrow}
          </span>
          <h2
            id="service-faq-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 max-w-md text-base text-navy/70">{subtitle}</p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.05 }}
          className="lg:col-span-8"
        >
          <Accordion
            type="single"
            collapsible
            defaultValue={`faq-${defaultOpenIndex}`}
            className="rounded-2xl border border-mist-line bg-white shadow-soft"
          >
            {faqs.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`faq-${i}`}
                data-testid={`${testId}-item-${i}`}
                className="border-b border-mist-line px-5 last:border-b-0"
              >
                <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-navy hover:no-underline sm:text-lg">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 pr-6 text-[15px] leading-relaxed text-navy/75">
                  {item.html ? (
                    <span dangerouslySetInnerHTML={{ __html: item.a }} />
                  ) : (
                    item.a
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
